import React, { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useHistory } from "react-router-dom";
import { Box } from "src/components/Box";
import { Button } from "src/components/Button";
import { Text } from "src/components/Text";
import { ArrowRight, Shop, User } from "@asphalt-react/iconpack";
import styled from "styled-components";
import { MediaQueries } from "src/constants/MediaQueries";
import { useTranslation } from "src/hooks/useTranslation";
import {
  IPassportBusinessType,
  usePassportStore,
} from "src/stores/Passport.store";
import { PASSPORT_ROUTES } from "src/constants/routes";
import { eventDispatcher } from "src/helpers/eventEmitter";
import { Logger } from "src/helpers/logger";
import { setCookie, CookieNames } from "src/helpers/cookies";
import { errFormatter } from "src/helpers/errorFormatter";
import { useErrorModalStore } from "src/components/ErrorModal/ErrorModal.store";
import { skipEntityTypeScreen } from "src/helpers/skipEntityTypeScreen";

const CardContainerEl = styled.div`
  margin-top: ${(p) => p.theme.dimension.spacing.xl}px;

  ${MediaQueries.screen.medium.minWidth} {
    margin-top: 50px;
    display: flex;
    overflow-x: scroll;
  }
`;

const CardEl = styled(Box)`
  background-color: ${(p) => p.theme.colors.background.white};
  border-radius: ${(p) => p.theme.layout.margin.s}px;
  border: 2px solid ${(p) => p.theme.colors.background.disabled};
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.08);
  padding: ${(p) => p.theme.layout.margin.m}px;

  ${MediaQueries.screen.medium.minWidth} {
    min-width: 433px;
    max-width: 433px;
    margin-right: ${(p) => p.theme.layout.margin.xl}px;
    padding: ${(p) => p.theme.layout.margin.xl}px;
  }
`;

const CardTitleEl = styled(Box)`
  padding-bottom: ${(p) => p.theme.layout.margin.m}px;

  ${MediaQueries.screen.medium.minWidth} {
    padding-bottom: 22px;
  }
`;

const DescriptionContainerEl = styled(Box)`
  min-height: 57px;
  margin-bottom: ${(p) => p.theme.layout.margin.s}px;

  ${MediaQueries.screen.medium.minWidth} {
    min-height: 72px;
    margin-bottom: ${(p) => p.theme.layout.margin.l}px;
  }
`;

const DescriptionEl = styled(Text)`
  font-size: ${(p) => p.theme.text.size.medium};

  ${MediaQueries.screen.medium.minWidth} {
    font-size: ${(p) => p.theme.text.size.large};
  }
`;

const ButtonWrapperEl = styled(Box)`
  border-top: 1px solid ${(p) => p.theme.colors.borders.appBar};
  padding-top: ${(p) => p.theme.layout.margin.m}px;
  text-align: right;

  ${MediaQueries.screen.medium.minWidth} {
    padding-top: ${(p) => p.theme.layout.margin.l}px;
  }
`;

const IconContainerEl = styled.div`
  width: 48px;
  height: 48px;
  background: ${(p) => p.theme.colors.borders.brand};
  border-radius: 100%;
  padding: 10px;

  path {
    fill: #fff;
  }

  svg {
    width: 30px;
    height: 30px;
  }

  ${MediaQueries.screen.medium.minWidth} {
    width: 57px;
    height: 57px;

    svg {
      width: 36px;
      height: 36px;
    }
  }
`;

const PageTitleEl = styled(Text)`
  ${MediaQueries.screen.medium.minWidth} {
    font-size: ${(p) => p.theme.text.size.xlarge};
  }
`;

const TitleEl = styled(Text)`
  font-size: ${(p) => p.theme.text.size.large};
  ${MediaQueries.screen.medium.minWidth} {
    font-size: ${(p) => p.theme.text.size.xlarge};
  }
`;

export const PassportEntityTypeScreen = observer(() => {
  const { push } = useHistory();
  const translate = useTranslation();
  const passportStore = usePassportStore();
  const { loadingState, selectedBusinessType, outlet } = passportStore;
  const errorModalStore = useErrorModalStore();
  useEffect(() => {
    eventDispatcher.publish({ action: "ob_entity_opened" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (skipEntityTypeScreen(outlet)) {
      setCookie(CookieNames.PassportStep, PASSPORT_ROUTES.paymentMethods);
      push(PASSPORT_ROUTES.paymentMethods);
    }
  }, [outlet, push]);

  const CardElement = observer(({ type }: { type: IPassportBusinessType }) => {
    const setEntityType = async () => {
      eventDispatcher.publish({ action: "ob_entity_next" });
      try {
        /**
         * TODO(boris): Add offline and error handling modal when it's refactored
         * @date - 22/10/20
         * @time - 16.26
         */
        passportStore.selectedBusinessType = type;
        await passportStore.updateOutlet({ business_type: type });
        setCookie(CookieNames.PassportStep, PASSPORT_ROUTES.paymentMethods);
        push(PASSPORT_ROUTES.paymentMethods);
        eventDispatcher.publish({
          action: "ob_select_entity",
          payload: {
            customer_id: passportStore.authUser.merchantId,
            email: passportStore.authUser.email,
            entity: type,
            current_time: new Date().toISOString(),
          },
        });
      } catch (err) {
        Logger.error(err);
        const { status, message } = errFormatter(err);
        eventDispatcher.publish({
          action: "ob_entity_error",
          payload: {
            message: message,
            httpStatus: status,
          },
        });
        errorModalStore.showAlert();
        errorModalStore.retryCta = setEntityType;
      }
    };

    const isLoading =
      loadingState.has.updateOutlet && type === selectedBusinessType;
    const isDisabled =
      loadingState.has.updateOutlet && type !== selectedBusinessType;

    return (
      <CardEl marginBottomXl>
        <CardTitleEl flex alignItemsCenter>
          <IconContainerEl>
            {type === "individual" ? <User /> : <Shop />}
          </IconContainerEl>
          <TitleEl fontWeight="demi" marginLeftM>
            {translate(`entity_type.${type}.title`)}
          </TitleEl>
        </CardTitleEl>
        <DescriptionContainerEl>
          <DescriptionEl fontWeight="book">
            {translate(`entity_type.${type}.description`)}
          </DescriptionEl>
        </DescriptionContainerEl>
        <ButtonWrapperEl>
          <Button
            nude
            iconAlign="right"
            loading={isLoading}
            icon={<ArrowRight />}
            disabled={isDisabled}
            onClick={setEntityType}
          >
            {translate(`entity_type.${type}.register`)}
          </Button>
        </ButtonWrapperEl>
      </CardEl>
    );
  });

  return (
    <>
      <PageTitleEl fontWeight="demi">
        {translate("entity_type.welcome.title")}
      </PageTitleEl>
      <CardContainerEl>
        <CardElement type="individual" />
        <CardElement type="corporate" />
      </CardContainerEl>
    </>
  );
});
